import type {
  CustomCellRendererProps,
  CustomHeaderProps,
} from '@mint-lib/ag-grid/react';
import { useCallback, useEffect, useReducer, useRef, useState } from 'react';

import type {
  ColDef,
  GridApi,
  IRowNode,
  RowSelectionOptions,
} from '@mint-lib/ag-grid/community';
import { Skeleton } from '@mint-lib/tw-components';

import { USER_STATUS } from '../../../constants/userTableV2.js';
import type { UserTableV2Row } from '../datasource.js';
import { isUserUpdating, type UserTableV2GridContext } from '../gridContext.js';

type SelectionState = boolean | 'indeterminate';
type NodesApi = Pick<GridApi<UserTableV2Row>, 'forEachNode'>;

export const createRowSelection = (currentUserId?: UserTableV2Row['id']) =>
  ({
    mode: 'multiRow',
    checkboxes: false,
    enableClickSelection: false,
    isRowSelectable: (node: IRowNode<UserTableV2Row>) =>
      node.data?.status !== USER_STATUS.DeactivatedSystem &&
      (currentUserId == null || node.data?.id !== currentUserId),
  }) satisfies RowSelectionOptions<UserTableV2Row>;

export const getLoadedSelectableNodes = (api: NodesApi) => {
  const nodes: IRowNode<UserTableV2Row>[] = [];

  api.forEachNode((node) => {
    if (node.data && node.selectable !== false) {
      nodes.push(node);
    }
  });

  return nodes;
};

export const getSelectionState = (
  nodes: IRowNode<UserTableV2Row>[],
): SelectionState => {
  if (!nodes.length) {
    return false;
  }

  const selectedCount = nodes.filter((node) => node.isSelected()).length;

  if (selectedCount === 0) {
    return false;
  }

  return selectedCount === nodes.length ? true : 'indeterminate';
};

const SelectionHeader = ({ api }: CustomHeaderProps<UserTableV2Row>) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [checked, setChecked] = useState<SelectionState>(false);
  const [hasLoadedRows, setHasLoadedRows] = useState(false);

  const refreshState = useCallback(() => {
    const nodes = getLoadedSelectableNodes(api);

    setHasLoadedRows(nodes.length > 0);
    setChecked(getSelectionState(nodes));
  }, [api]);

  useEffect(() => {
    refreshState();
    api.addEventListener('modelUpdated', refreshState);
    api.addEventListener('selectionChanged', refreshState);

    return () => {
      if (!api.isDestroyed()) {
        api.removeEventListener('modelUpdated', refreshState);
        api.removeEventListener('selectionChanged', refreshState);
      }
    };
  }, [api, refreshState]);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.indeterminate = checked === 'indeterminate';
    }
  }, [checked]);

  const handleChange = useCallback(() => {
    const nodes = getLoadedSelectableNodes(api);
    const nextValue = checked !== true;

    api.setNodesSelected({ nodes, newValue: nextValue });
    setChecked(nextValue && nodes.length > 0);
  }, [api, checked]);

  const wrapperClass = [
    'ag-wrapper ag-checkbox-input-wrapper',
    checked === true && 'ag-checked',
    checked === 'indeterminate' && 'ag-indeterminate',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className="flex h-full items-center justify-center">
      <div className="ag-labeled ag-label-align-right ag-checkbox ag-input-field">
        <div className={wrapperClass}>
          <input
            ref={inputRef}
            className="ag-input-field-input ag-checkbox-input"
            type="checkbox"
            checked={checked === true}
            disabled={!hasLoadedRows}
            aria-label="Select loaded users"
            onChange={handleChange}
          />
        </div>
      </div>
    </div>
  );
};

const SelectionCellSkeleton = () => (
  <div className="flex h-full w-full items-center justify-center">
    <Skeleton variant="rounded" className="size-4" />
  </div>
);

type SelectionCellProps = CustomCellRendererProps<
  UserTableV2Row,
  unknown,
  UserTableV2GridContext
>;

const SelectionCell = ({ context, data, node }: SelectionCellProps) => {
  const [, forceUpdate] = useReducer((tick: number) => tick + 1, 0);
  const isUpdating = isUserUpdating(context, data?.id);

  useEffect(() => {
    if (!node) return;
    node.addEventListener('rowSelected', forceUpdate);
    return () => {
      node.removeEventListener('rowSelected', forceUpdate);
    };
  }, [node]);

  if (!data || isUpdating) {
    return <SelectionCellSkeleton />;
  }

  const checked = node.isSelected() === true;
  const isSelectable = node.selectable;
  const wrapperClass = [
    'ag-wrapper ag-checkbox-input-wrapper',
    checked && 'ag-checked',
    !isSelectable && 'opacity-40 pointer-events-none',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className="flex h-full items-center justify-center">
      <div className="ag-labeled ag-label-align-right ag-checkbox ag-input-field">
        <div className={wrapperClass}>
          <input
            className="ag-input-field-input ag-checkbox-input"
            type="checkbox"
            checked={checked}
            disabled={!isSelectable}
            aria-label="Select row"
            onChange={(event) => node.setSelected(event.target.checked)}
          />
        </div>
      </div>
    </div>
  );
};

export const selectionColumn = {
  colId: 'selection',
  headerName: '',
  headerComponent: SelectionHeader,
  cellRenderer: SelectionCell,
  loadingCellRenderer: SelectionCellSkeleton,
  pinned: 'left',
  maxWidth: 36,
  minWidth: 36,
  width: 36,
  resizable: false,
  sortable: false,
  suppressHeaderContextMenu: true,
  suppressHeaderMenuButton: true,
  suppressMovable: true,
} satisfies ColDef<UserTableV2Row>;
